import type { RefObject } from "react";
import { useEffect } from "react";

/**
 * Closes a pinned citation tooltip on a press outside both the tooltip and the
 * chip that opened it, or on Escape. Does nothing while the tooltip is not
 * pinned (hover-only tooltips close on mouse leave, as before).
 */
export const usePinnedTooltipDismiss = (
  pinned: boolean,
  tooltipRef: RefObject<HTMLElement>,
  anchorEl: HTMLElement | null,
  onClose: () => void
): void => {
  useEffect(() => {
    if (!pinned) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;

      if (!target) return;
      if (tooltipRef.current?.contains(target) || anchorEl?.contains(target)) return;
      onClose();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [pinned, tooltipRef, anchorEl, onClose]);
};
